import React, { useState, useRef } from 'react';
import { Mic, Square, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const VoiceRecorder = ({ token, memberId, onReportGenerated }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await sendAudioToBackend(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Impossible d\'accéder au microphone. Veuillez autoriser l\'accès.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const sendAudioToBackend = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      if (memberId) {
        formData.append('member_id', memberId);
      }

      const response = await axios.post(
        `${API}/notes/voice-to-report`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      onReportGenerated(response.data);
    } catch (error) {
      console.error('Error processing audio:', error);
      alert('Erreur lors du traitement audio. Veuillez réessayer.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-gradient-to-br from-secondary-50 to-primary-50 rounded-2xl p-4 md:p-6 border-2 border-secondary-100">
      <div className="flex items-center gap-3 mb-3 md:mb-4">
        <Mic className="w-5 h-5 md:w-6 md:h-6 text-secondary flex-shrink-0" />
        <h3 className="font-bold text-secondary-900 text-base md:text-lg">Parlez → Votre rapport est prêt</h3>
      </div>

      <p className="text-xs md:text-sm text-slate-600 mb-3 md:mb-4">
        Dictez vos observations, l'IA les transformera en rapport professionnel structuré
      </p>

      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        {!isRecording && !isProcessing && (
          <Button
            data-testid="start-recording-btn"
            onClick={startRecording}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <Mic className="w-5 h-5" />
            Commencer à parler
          </Button>
        )}

        {isRecording && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              data-testid="stop-recording-btn"
              onClick={stopRecording}
              className="bg-error hover:bg-error/90 flex items-center gap-2"
            >
              <Square className="w-5 h-5" />
              Arrêter ({formatTime(recordingTime)})
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-error rounded-full animate-pulse"></div>
              <span className="text-xs md:text-sm text-slate-600">Enregistrement...</span>
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="flex items-center gap-3">
            <Loader className="w-5 h-5 text-secondary animate-spin" />
            <span className="text-xs md:text-sm text-slate-600">
              Génération du rapport en cours...
            </span>
          </div>
        )}
      </div>

      <div className="mt-3 md:mt-4 text-xs text-slate-500">
        Astuce : Parlez clairement et décrivez vos observations. L'IA se charge de la mise en forme.
      </div>
    </div>
  );
};

export default VoiceRecorder;
